/* ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THIS FILE IS A COPY. THE ORIGINAL LIVES IN THE WEB REPO:
 *     HypeTunes_Web_2026/src/components/three/shoulderBand.js
 *
 * Keeps every body's shoulders in a natural band whatever clip it plays, so no
 * body needs its skeleton tuned by hand (poseFix.js runs it). One implementation,
 * shared like faceRig/clipRetarget.
 *
 * ⚠ DO NOT EDIT BELOW THE SENTINEL. Fix the Web original, then:
 *     node scripts/check_face_decal_sync.mjs --sync
 * ───────────────────────────────────────────────────────────────────────────── */

/* ── VERBATIM FROM HypeTunes_Web_2026 BELOW THIS LINE ── */
import * as THREE from 'three'

/**
 * THE SHOULDER BAND — every clip, every body, shoulders where a person's are,
 * with nobody tuning a slider.
 *
 * Khari, 2026-10-28: *"I can see that the models have shoulders too high. This
 * is what happened before and we had to add extra handling for that within the
 * editor"* … *"see if you can make the 3d models work without the user having
 * to make fixes to the skeleton."*
 *
 * ⚠ IT IS THE CLIP, NOT THE RIG. Measured on 15 bodies (2026-10-28,
 * `docs/3d_body_renewal/FINDINGS.md` §13): every body's bind clavicle is level
 * (0°), and each clip then sets the SAME clavicle angle on every body, to a
 * tenth of a degree, whatever rig run it came from. Against the torso:
 *
 *     boom_dance −27   stand_and_drink −22   hip_hop −21   listening −10
 *     talk_hands_open −4   rap_inplace 0   talk_passionately +19   cheer +19
 *     idle_3 +20   joyful_dance +24   Idle_6 +25   shrug +27
 *
 * A fixed offset (`shoulderDrop`) cannot be right for a range that wide. Owners
 * tuned it to +10 while looking at `listening_gesture`, which brings that clip
 * to level, and the same +10 then lands on every clip: talk and dance clips
 * reach +30–35°, shoulders at the ears. That is the "too high".
 *
 * So, after the owner's offset: take each clavicle's elevation against the
 * TORSO (chest bone → neck, so a crouch or a seated lean is not read as a
 * shrug), LOW-PASS it, and pull the low-passed value back inside the band. The
 * low-pass is the point. The STANCE a clip holds is corrected; the MOTION
 * inside it (a shrug, a bounce on the beat) passes through untouched, because
 * it is gone before the filter has followed it.
 *
 * ⚠ THE ARM KEEPS ITS DIRECTION. Turning the clavicle carries the whole arm
 * round with it (the Model Lab's own hint: "past a few degrees the forearm ends
 * up inside the hip"). The band turns the upper arm back by the same angle
 * about the same WORLD axis, so the shoulder joint moves and the arm does not.
 *
 * ⚠ RUN IT INSIDE THE CALLER'S IDEMPOTENT CORRECTION WINDOW: after
 * `beginCorrection` has reset the Shoulder AND Arm bones, after the owner's
 * `shoulderDrop`, before `armOut`. It adds to the bones like every other
 * correction, so it must land on a clean value exactly once a frame.
 */

/** Degrees, clavicle elevation against the torso. Level is 0. */
export const SHOULDER_BAND = { lo: -8, hi: 8, tau: 0.5 }

/* THE DEFAULT `shoulderDrop` FOR A BODY NOBODY HAS TUNED. Six of the nine
   hand-tuned bodies sit at 9–11, and all of them were tuned on
   `listening_gesture`, the pose every body rests in: +10 takes its −10° to
   level and gives the arms daylight at the hips. Used wherever a stored pose has
   no `shoulderDrop` at all; an owner's own value, including 0, always wins. */
export const AUTO_SHOULDER_DROP = 10

/** The owner's `shoulderDrop`, or the default when they never set one. */
export function resolveShoulderDrop(pose) {
  const v = pose?.shoulderDrop
  return v === undefined || v === null || v === '' || !Number.isFinite(Number(v)) ? AUTO_SHOULDER_DROP : Number(v)
}

const DEG = Math.PI / 180
// The same axis the owner's `shoulderDrop` turns about. `rotateOnWorldAxis`
// premultiplies, so it is the PARENT's (the chest's) local Z, not world Z, which
// is why the correction holds while the body turns to face somebody.
const LATERAL = new THREE.Vector3(0, 0, 1)
const WORLD_UP = new THREE.Vector3(0, 1, 0)

/**
 * @param {THREE.Object3D} root a cloned, skeleton-safe body
 * @returns {{usable: boolean, apply: (dt: number, opts?: object) => ({left: number, right: number, moved: number}|null), reset: () => void}}
 */
export function createShoulderBand(root) {
  const byName = (n) => root.getObjectByName(n) || null
  let neck = null
  root.traverse((o) => { if (!neck && o.isBone && (o.name || '').toLowerCase().endsWith('neck')) neck = o })
  const S = {
    Left: { sh: byName('LeftShoulder'), arm: byName('LeftArm'), lp: null, sign: 1 },
    Right: { sh: byName('RightShoulder'), arm: byName('RightArm'), lp: null, sign: -1 },
  }
  const usable = !!(S.Left.sh?.parent && S.Left.arm && S.Right.sh?.parent && S.Right.arm)

  const pA = new THREE.Vector3()
  const pS = new THREE.Vector3()
  const pT = new THREE.Vector3()
  const up = new THREE.Vector3()
  const qP = new THREE.Quaternion()
  const qS = new THREE.Quaternion()
  const qc = new THREE.Quaternion()
  const axisW = new THREE.Vector3()
  const axisL = new THREE.Vector3()

  /** Elevation of the arm joint above the clavicle root, against the torso, in degrees. */
  const elevation = (s) => {
    s.arm.updateWorldMatrix(true, false)
    pA.setFromMatrixPosition(s.arm.matrixWorld)
    pS.setFromMatrixPosition(s.sh.matrixWorld)
    if (neck) {
      neck.updateWorldMatrix(true, false)
      pT.setFromMatrixPosition(s.sh.parent.matrixWorld)
      up.setFromMatrixPosition(neck.matrixWorld).sub(pT)
      if (up.lengthSq() < 1e-12) up.copy(WORLD_UP)
      else up.normalize()
    } else {
      up.copy(WORLD_UP)
    }
    const d = pA.sub(pS)
    const len = d.length()
    if (len < 1e-9) return 0
    return Math.asin(Math.max(-1, Math.min(1, d.dot(up) / len))) / DEG
  }

  /** Turn the clavicle by `deg` (+ = up) and the upper arm back by the same world angle. */
  const turn = (s, deg) => {
    s.sh.parent.getWorldQuaternion(qP)
    axisW.copy(LATERAL).applyQuaternion(qP)
    s.sh.quaternion.premultiply(qc.setFromAxisAngle(LATERAL, s.sign * deg * DEG))
    qS.copy(qP).multiply(s.sh.quaternion).invert()
    axisL.copy(axisW).applyQuaternion(qS)
    s.arm.quaternion.premultiply(qc.setFromAxisAngle(axisL, -s.sign * deg * DEG))
  }

  /**
   * One frame. `dt` in seconds. `opts` may override `lo`, `hi` and `tau`;
   * `tau: 0` corrects the instantaneous angle (for measuring a clip offline).
   */
  const apply = (dt, opts = {}) => {
    if (!usable) return null
    const lo = opts.lo ?? SHOULDER_BAND.lo
    const hi = opts.hi ?? SHOULDER_BAND.hi
    const tau = opts.tau ?? SHOULDER_BAND.tau
    /* A long frame (a hidden tab coming back) is a fresh start, not a reason to
       swing the filter across a whole clip in one step. */
    const step = Number.isFinite(dt) && dt > 0 ? Math.min(dt, 0.25) : 1 / 60
    const k = tau > 0 ? 1 - Math.exp(-step / tau) : 1
    let moved = 0
    for (const side of ['Left', 'Right']) {
      const s = S[side]
      const e = elevation(s)
      s.lp = s.lp === null ? e : s.lp + (e - s.lp) * k
      const c = s.lp > hi ? hi - s.lp : s.lp < lo ? lo - s.lp : 0
      if (Math.abs(c) > 0.01) turn(s, c)
      if (Math.abs(c) > Math.abs(moved)) moved = c
    }
    return { left: S.Left.lp, right: S.Right.lp, moved }
  }

  /** Forget the filter, e.g. when a different body is loaded into the same viewer. */
  const reset = () => { S.Left.lp = null; S.Right.lp = null }

  return { usable, apply, reset }
}

export default createShoulderBand
